import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  MdAutoAwesome,
  MdMenu,
  MdStorefront,
  MdClose,
  MdContentCopy,
  MdCheck,
} from "react-icons/md";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import useQueueCount from "../../customHook/Queue/useQueueCount";
import useGenerationCount from "../../customHook/Generation/useGenerationCount";

const Header = ({ toggleSidebar }) => {
  const queueCount = useQueueCount();
  const reduxStoreName = useSelector((state) => state.auth?.storeName);
  const reduxRole = useSelector((state) => state.auth?.role);

  const storeName =
    reduxStoreName ||
    (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("storeName") : "") ||
    "Anjali Test Store";

  const role =
    reduxRole ||
    (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("userRole") : "") ||
    "Admin";

  const formattedRole = role
    ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
    : "Admin";

  const [isStorePopoverOpen, setIsStorePopoverOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 250 });

  const storeBtnRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePopoverPosition = useCallback(() => {
    if (!storeBtnRef.current) return;
    const rect = storeBtnRef.current.getBoundingClientRect();
    const popoverWidth = Math.min(250, window.innerWidth - 24);

    let left = rect.left + rect.width / 2 - popoverWidth / 2;
    if (left + popoverWidth > window.innerWidth - 12) {
      left = window.innerWidth - 12 - popoverWidth;
    }
    if (left < 12) {
      left = 12;
    }

    setPopoverPosition({
      top: rect.bottom + 6,
      left: Math.round(left),
      width: popoverWidth,
    });
  }, []);

  const toggleStorePopover = () => {
    setIsStorePopoverOpen((prev) => {
      if (!prev) {
        setTimeout(updatePopoverPosition, 0);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (!isStorePopoverOpen) return;

    updatePopoverPosition();

    const handleScrollOrResize = () => {
      updatePopoverPosition();
    };

    const handleClickOutside = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        storeBtnRef.current &&
        !storeBtnRef.current.contains(e.target)
      ) {
        setIsStorePopoverOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsStorePopoverOpen(false);
      }
    };

    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isStorePopoverOpen, updatePopoverPosition]);

  const handleCopyStoreName = async (e) => {
    e?.stopPropagation();
    if (!storeName) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(storeName);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = storeName;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setIsCopied(true);
      toast.success("Store name copied!");
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy store name:", err);
      toast.error("Failed to copy store name");
    }
  };

  const {
    generationsLeft,
    iMaxAllowedGeneration,
    iGenerationCount,
  } = useGenerationCount();

  const displayGenerationsLeft =
    generationsLeft !== null && generationsLeft !== undefined ? generationsLeft : 0;

  const generationTooltip = `Generations Left: ${displayGenerationsLeft}${
    iMaxAllowedGeneration !== null && iMaxAllowedGeneration > 0 ? ` / ${iMaxAllowedGeneration}` : ""
  }${iGenerationCount !== null && iGenerationCount !== undefined ? ` (Used: ${iGenerationCount})` : ""}`;

  const generationStatus =
    displayGenerationsLeft === 0 ? "empty" : displayGenerationsLeft <= 2 ? "warning" : "normal";

  const generationStyles = {
    normal: {
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold",
      icon: "text-emerald-500",
    },
    warning: {
      badge: "bg-amber-50 text-amber-700 border-amber-200 font-semibold",
      icon: "text-amber-500",
    },
    empty: {
      badge: "bg-rose-50 text-rose-700 border-rose-200 font-semibold",
      icon: "text-rose-500",
    },
  }[generationStatus] || {
    badge: "bg-slate-50 text-slate-600 border-slate-200 font-semibold",
    icon: "text-slate-400",
  };

  const queueBadge =
    queueCount > 0
      ? "bg-blue-50 text-blue-700 border-blue-200 font-semibold"
      : "bg-slate-50 text-slate-600 border-slate-200";

  return (
    <>
      {/* Sticky Header */}
      <header className="app-header sticky top-0 z-40 h-14 shrink-0">
        <div className="flex h-full items-center justify-between px-4 sm:px-6 gap-3">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              onClick={toggleSidebar}
              className="lg:hidden text-secondary hover:text-primary p-1 hover:bg-surface-container rounded-lg transition-all cursor-pointer focus:outline-none border-none bg-transparent flex items-center justify-center shrink-0"
              type="button"
              aria-label="Toggle menu"
            >
              <MdMenu size={22} />
            </button>
            <h1 className="font-headline-lg text-[16px] leading-7 text-primary font-bold tracking-tight shrink-0">
              VTO Garments
            </h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {storeName && (
              <button
                ref={storeBtnRef}
                onClick={toggleStorePopover}
                type="button"
                className={`h-8 flex items-center gap-1.5 px-2.5 rounded-full border bg-white text-slate-700 text-[12px] font-medium shadow-sm transition-all cursor-pointer min-w-0 max-w-[140px] xs:max-w-[190px] sm:max-w-[260px] md:max-w-[340px] focus:outline-none select-none ${
                  isStorePopoverOpen
                    ? "border-[#4F46E5] ring-2 ring-[#EEF2FF] bg-indigo-50/20"
                    : "border-[#C4CAD6] hover:border-[#4F46E5] hover:bg-slate-50"
                }`}
                title={`Current Store: ${storeName} (Click to view details)`}
                aria-label={`Current Store: ${storeName}`}
                aria-haspopup="dialog"
                aria-expanded={isStorePopoverOpen}
              >
                <MdStorefront size={16} className="text-[#4F46E5] shrink-0" />
                <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">
                  Store:
                </span>
                <span className="truncate font-semibold text-slate-800">
                  {storeName}
                </span>
              </button>
            )}

            <div className="app-status-rail shrink-0" title={generationTooltip}>
              <div
                className="flex items-center gap-1.5 px-2"
                aria-label={generationTooltip}
              >
                <MdAutoAwesome size={15} className={generationStyles.icon} />
                <span className="hidden sm:inline text-[12px] font-medium text-slate-600">
                  Generations
                </span>
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[11px] font-bold rounded-full border tabular-nums ${generationStyles.badge}`}
                >
                  {displayGenerationsLeft}
                </span>
              </div>

              <span className="h-3.5 w-px bg-[#D1D5DB]" aria-hidden="true" />

              <div
                className="flex items-center gap-1.5 px-2"
                title={`Tasks in processing queue: ${queueCount}`}
                aria-label={`Tasks in processing queue: ${queueCount}`}
              >
                <span className="text-[12px] font-medium text-slate-600">Queue</span>
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[11px] font-bold rounded-full border tabular-nums ${queueBadge}`}
                >
                  {queueCount}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Ultra-Slick Active Store Popover Card (2 compact rows, no dedicated footer row) */}
      {isStorePopoverOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              width: `${popoverPosition.width}px`,
              zIndex: 9999,
            }}
            className="bg-white rounded-xl shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.04)] border border-slate-100 p-3 transition-all duration-150 animate-in fade-in select-none"
            role="dialog"
            aria-label="Active Store Details"
          >
            {/* Row 1: Active Store badge + Role badge (left) and Close button (right) */}
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10px] font-bold tracking-wider uppercase shrink-0">
                  <MdStorefront size={13} className="shrink-0 text-[#4F46E5]" />
                  <span>Active Store</span>
                </div>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-slate-500 bg-slate-100 font-medium text-[10px] shrink-0">
                  {formattedRole}
                </span>
              </div>

              <button
                onClick={() => setIsStorePopoverOpen(false)}
                type="button"
                className="text-slate-400 hover:text-slate-600 p-0.5 rounded hover:bg-slate-100 transition-colors focus:outline-none cursor-pointer shrink-0 ml-2"
                aria-label="Close active store details"
              >
                <MdClose size={16} />
              </button>
            </div>

            {/* Row 2: Store Name + Inline Copy Icon */}
            <div className="pt-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Store Name
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span
                  className="text-[14px] font-semibold text-slate-900 truncate"
                  title={storeName}
                >
                  {storeName}
                </span>
                <button
                  onClick={handleCopyStoreName}
                  type="button"
                  className="inline-flex items-center justify-center p-1 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50/70 rounded transition-colors focus:outline-none cursor-pointer shrink-0"
                  title={isCopied ? "Copied!" : "Copy store name"}
                  aria-label="Copy store name"
                >
                  {isCopied ? (
                    <MdCheck size={14} className="text-emerald-600" />
                  ) : (
                    <MdContentCopy size={13} />
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Header;
